import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../shared/interfaces';
import {ApiTagClassificationItem} from '../components/dashboard/dashboardTypes';
import {t} from 'i18next';
import {Assignment} from '../api/types/production.type';

interface ApiTagItem {
  id: string;
  text: string;
  parent: string;
  isGroup?: boolean;
}
type TreeItem = TreeViewBaseItem<ExtendedTreeItemProps>;
const CLASSIFICATION_ROOT_ID = 'root:tag-classifications';
const CLASSIFICATION_ROOT_LABEL = t('tagClassification.rootLabel');

export const formatTagTreeData = (
  apiData: ApiTagItem[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const lookup: Record<string, TreeViewBaseItem<ExtendedTreeItemProps>> = {};
  const rootNodes: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  apiData.forEach((item) => {
    lookup[item.id] = {
      id: item.id,
      label: item.text,
      name: item.text.replace(/\s+/g, '_').toLocaleLowerCase(),
      children: [],
    };
  });

  apiData.forEach((item) => {
    if (item.parent && item.parent !== '#' && lookup[item.parent]) {
      lookup[item.parent].children!.push(lookup[item.id]);
    } else {
      rootNodes.push(lookup[item.id]);
    }
  });

  return rootNodes;
};

export const formatAssignmentsTreeData = (
  apiData: Assignment[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const lookup: Record<string, TreeViewBaseItem<ExtendedTreeItemProps>> = {};
  const rootNodes: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  apiData.forEach((item) => {
    lookup[item.id] = {
      id: item.id,
      label: item.text,
      name: item.text.replace(/\s+/g, '_').toLocaleLowerCase(),
      children: [],
    };
  });

  apiData.forEach((item) => {
    if (item.parent && item.parent !== '#' && lookup[item.parent]) {
      lookup[item.parent].children!.push(lookup[item.id]);
    } else if (item.parent === '#') {
      rootNodes.push(lookup[item.id]);
    }
  });
  return rootNodes;
};

export const formatTagClassificationTreeData = (
  apiData: ApiTagClassificationItem[],
  options?: {
    wrapWithRoot?: boolean;
    rootId?: string;
    rootLabel?: string;
    includeCountInLabel?: boolean;
    sort?: boolean;
  }
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const {
    wrapWithRoot = true,
    rootId = CLASSIFICATION_ROOT_ID,
    rootLabel = CLASSIFICATION_ROOT_LABEL,
    includeCountInLabel = false,
  } = options ?? {};
  const lookup: Record<string, TreeViewBaseItem<ExtendedTreeItemProps>> = {};
  const roots: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  for (const item of apiData) {
    const id = String(item.classificationID);
    const rawLabel = (item.classification ?? '').trim();

    const label = includeCountInLabel ? rawLabel : rawLabel;

    lookup[id] = {
      id,
      label,
      name: label.replace(/\s+/g, '_').toLocaleLowerCase(),
      children: [],
      type: 'classification',
      added: item.isSelected ?? undefined,
      disabled: item.isLocked ?? undefined,
    };
  }

  for (const item of apiData) {
    const id = String(item.classificationID);
    const parentIdRaw = item.parentID;
    const parentId =
      parentIdRaw !== undefined &&
      parentIdRaw !== null &&
      String(parentIdRaw) !== '' &&
      String(parentIdRaw) !== '0'
        ? String(parentIdRaw)
        : null;

    if (parentId && lookup[parentId]) {
      lookup[parentId].children!.push(lookup[id]);
    } else {
      roots.push(lookup[id]);
    }
  }
  const annotate = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    for (const n of nodes) {
      const c = n.children?.length ?? 0;
      n.hasChildren = c > 0;
      n.numberOfChild = c;
      if (n.children && c) annotate(n.children);
    }
  };
  annotate(roots);
  if (!wrapWithRoot) return roots;
  if (roots.length === 0) {
    return [];
  }
  const rootNode: TreeViewBaseItem<ExtendedTreeItemProps> = {
    id: rootId,
    label: rootLabel,
    name: rootLabel.replace(/\s+/g, '_').toLocaleLowerCase(),
    children: roots,
    type: 'classification-root',
    hasChildren: roots.length > 0,
    numberOfChild: roots.length,
    disabled: false,
  };
  return [rootNode];
};

export function wrapWithRootNode(
  children: TreeItem[] = [],
  opts: {
    id: string;
    label: string;
    type?: string;
    disabled?: boolean;
  }
): TreeItem[] {
  const {id, label, type = 'root', disabled = false} = opts;

  const root: TreeItem = {
    id,
    label,
    name: label.replace(/\s+/g, '_').toLowerCase(),
    children,
    type: type as ExtendedTreeItemProps['type'],
    hasChildren: children.length > 0,
    numberOfChild: children.length,
    disabled,
  };

  return [root];
}
